/* ─────────────────────────────────────────────────────────────
   Medicore quick order pad

   Resolves SKUs and product names to variants, then adds the whole
   list to the cart in one request.

   SKU resolution takes two steps because no single storefront
   endpoint does it: /search/suggest.json finds candidate products by
   SKU but will not say which variant matched, so the candidate is
   then fetched from /products/{handle}.js and its variants are
   compared exactly. That second step is what makes "NON25212" land
   on the right box size rather than on the product's first variant.

   Everything runs against public storefront endpoints - no app, no
   customer account, no server state.
   ───────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var DEBOUNCE = 250;
  var SUGGEST_LIMIT = 6;

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function money(cents, format) {
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function delimit(number, precision, thousands, decimal) {
      if (isNaN(number) || number === null) return '0';
      var parts = (number / 100).toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }

    var match = format.match(placeholder);
    var value;

    switch (match && match[1]) {
      case 'amount_no_decimals': value = delimit(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = delimit(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = delimit(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = delimit(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = delimit(cents, 0, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = delimit(cents, 2, ' ', '.'); break;
      default: value = delimit(cents, 2, ',', '.');
    }

    return format.replace(placeholder, value);
  }

  function sized(url, width) {
    if (!url) return '';
    var absolute = url.indexOf('//') === 0 ? 'https:' + url : url;
    return absolute + (absolute.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* Cache by handle: a buyer working through a spreadsheet often has
     several lines from the same product in different sizes. */
  var productCache = {};

  function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];

    productCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (response) {
        if (!response.ok) return null;
        return response.json();
      })
      .catch(function () { return null; });

    return productCache[handle];
  }

  function suggest(query) {
    var url =
      '/search/suggest.json?q=' + encodeURIComponent(query) +
      '&resources[type]=product' +
      '&resources[limit]=' + SUGGEST_LIMIT +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=title,vendor,product_type,variants.sku,variants.title';

    return fetch(url)
      .then(function (response) {
        if (!response.ok) return [];
        return response.json();
      })
      .then(function (data) {
        return (data && data.resources && data.resources.results && data.resources.results.products) || [];
      })
      .catch(function () { return []; });
  }

  function matchVariantBySku(product, query) {
    if (!product || !product.variants) return null;
    var needle = String(query).trim().toLowerCase();
    if (!needle) return null;

    for (var i = 0; i < product.variants.length; i++) {
      var sku = product.variants[i].sku;
      if (sku && String(sku).trim().toLowerCase() === needle) return product.variants[i];
    }
    return null;
  }

  function firstSellable(product) {
    if (!product || !product.variants) return null;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available) return product.variants[i];
    }
    return product.variants[0] || null;
  }

  var QuickOrderPad = class extends HTMLElement {
    connectedCallback() {
      this.template = this.querySelector('[data-qop-row-template]');
      this.rowsEl = this.querySelector('[data-qop-rows]');
      if (!this.template || !this.rowsEl) return;

      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.maxRows = parseInt(this.dataset.maxRows, 10) || 40;
      this.statusEl = this.querySelector('[data-qop-status]');
      this.countEl = this.querySelector('[data-qop-count]');
      this.totalEl = this.querySelector('[data-qop-total]');
      this.submitEl = this.querySelector('[data-qop-submit]');
      this.emptyLabel = this.countEl ? this.countEl.textContent : '';

      this.bind();

      var start = parseInt(this.dataset.startRows, 10) || 5;
      for (var i = 0; i < start; i++) this.addRow();
      this.refresh();
    }

    bind() {
      var self = this;

      this.addEventListener('click', function (event) {
        var target = event.target;

        if (target.closest('[data-qop-addrow]')) {
          var row = self.addRow();
          if (row) row.querySelector('[data-qop-input]').focus();
          return;
        }

        if (target.closest('[data-qop-remove]')) {
          var toRemove = target.closest('[data-qop-row]');
          if (toRemove) toRemove.remove();
          if (!self.rowsEl.children.length) self.addRow();
          self.refresh();
          return;
        }

        if (target.closest('[data-qop-change]')) {
          self.unresolve(target.closest('[data-qop-row]'));
          return;
        }

        if (target.closest('[data-qop-clear]')) {
          self.clear();
          return;
        }

        if (target.closest('[data-qop-submit]')) {
          self.submit();
          return;
        }

        if (target.closest('[data-qop-lookup]')) {
          self.lookupPasted();
          return;
        }

        var mode = target.closest('[data-qop-mode]');
        if (mode) self.setMode(mode.dataset.qopMode);

        var step = target.closest('[data-qop-plus], [data-qop-minus]');
        if (step) {
          var stepRow = step.closest('[data-qop-row]');
          var input = stepRow.querySelector('[data-qop-qty]');
          var next = (parseInt(input.value, 10) || 1) + (step.hasAttribute('data-qop-plus') ? 1 : -1);
          input.value = Math.max(1, next);
          self.refresh();
        }

        // A click anywhere else closes any open suggestion list.
        if (!target.closest('[data-qop-results]') && !target.closest('[data-qop-input]')) {
          self.closeAllResults();
        }
      });

      this.addEventListener('input', function (event) {
        if (event.target.matches('[data-qop-input]')) self.onSearch(event.target);
        if (event.target.matches('[data-qop-qty]')) self.refresh();
      });

      this.addEventListener('change', function (event) {
        if (event.target.matches('[data-qop-variant]')) {
          self.chooseVariant(event.target.closest('[data-qop-row]'), event.target.value);
        }
      });

      this.addEventListener('keydown', function (event) {
        if (event.target.matches('[data-qop-input]')) self.onKeydown(event);
      });

      /* The picker hands back variant ids and handles. Each one is
         resolved through the same product fetch the inline search
         uses, so a picked row and a typed row end up identical. */
      document.addEventListener('quick-order:selected', function (event) {
        var detail = event.detail || {};
        if (!detail.items || !detail.items.length) return;
        if (detail.trigger && !self.contains(detail.trigger)) return;
        self.receive(detail.items, detail.trigger);
      });
    }

    receive(items, trigger) {
      var self = this;
      var startRow = trigger ? trigger.closest('[data-qop-row]') : null;

      var work = items.reduce(function (chain, item) {
        return chain.then(function () {
          return fetchProduct(item.handle).then(function (product) {
            if (!product) return null;

            var variant = product.variants.filter(function (option) {
              return String(option.id) === String(item.id);
            })[0];
            if (!variant) return null;

            var row = startRow;
            startRow = null;
            if (!row) row = self.lastEmptyRow() || self.addRow();
            if (!row) return null;

            self.resolve(row, product, variant, product.variants.length > 1);
            row.querySelector('[data-qop-qty]').value = item.quantity || 1;
            return null;
          });
        });
      }, Promise.resolve());

      work.then(function () {
        self.refresh();
        self.say(
          items.length + ' item' + (items.length === 1 ? '' : 's') + ' added to your list. Check the quantities, then add to cart.',
          'ok'
        );
      });
    }

    /* ── rows ── */

    addRow() {
      if (this.rowsEl.children.length >= this.maxRows) {
        this.say('Maximum of ' + this.maxRows + ' rows reached.', 'error');
        return null;
      }

      var fragment = this.template.content.cloneNode(true);
      var row = fragment.querySelector('[data-qop-row]');

      // Read on small screens, where the columns have no headings.
      row.querySelector('[data-qop-price]').setAttribute('data-label', 'Price');
      row.querySelector('[data-qop-sub]').setAttribute('data-label', 'Subtotal');

      this.rowsEl.appendChild(fragment);
      return row;
    }

    clear() {
      this.rowsEl.innerHTML = '';
      var paste = this.querySelector('.qop__paste');
      if (paste) paste.value = '';
      var start = parseInt(this.dataset.startRows, 10) || 5;
      for (var i = 0; i < start; i++) this.addRow();
      this.say('');
      this.refresh();
    }

    lastEmptyRow() {
      var rows = this.rowsEl.querySelectorAll('[data-qop-row]');
      for (var i = 0; i < rows.length; i++) {
        if (!rows[i].variant && !rows[i].querySelector('[data-qop-input]').value.trim()) return rows[i];
      }
      return null;
    }

    /* ── search ── */

    onSearch(input) {
      var self = this;
      var row = input.closest('[data-qop-row]');
      var query = input.value.trim();

      window.clearTimeout(row.searchTimer);
      this.setLoading(row, false);

      if (query.length < 2) {
        this.closeResults(row);
        return;
      }

      row.searchTimer = window.setTimeout(function () {
        self.setLoading(row, true);

        // Only the newest query for this row may paint results.
        var token = (row.searchToken = (row.searchToken || 0) + 1);

        suggest(query).then(function (products) {
          if (token !== row.searchToken) return;
          self.setLoading(row, false);
          self.showResults(row, products, query);
        });
      }, DEBOUNCE);
    }

    showResults(row, products, query) {
      var list = row.querySelector('[data-qop-results]');
      var input = row.querySelector('[data-qop-input]');

      if (!products.length) {
        list.innerHTML = '<li class="qop-result__empty">Nothing found for &ldquo;' + esc(query) + '&rdquo;</li>';
        list.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        return;
      }

      list.innerHTML = products
        .map(function (product, index) {
          var image = product.image || product.featured_image || '';
          var sub = [product.vendor, product.product_type].filter(Boolean).join(' · ');

          return '<li class="qop-result" role="option" id="qop-opt-' + index + '"' +
            ' aria-selected="false" data-handle="' + esc(product.handle) + '">' +
            (image
              ? '<img class="qop-result__thumb" src="' + esc(sized(image, 80)) + '" alt="" loading="lazy">'
              : '<span class="qop-result__thumb"></span>') +
            '<span class="qop-result__body">' +
            '<span class="qop-result__title">' + esc(product.title) + '</span>' +
            (sub ? '<span class="qop-result__sub">' + esc(sub) + '</span>' : '') +
            '</span></li>';
        })
        .join('');

      list.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      row.activeIndex = -1;

      var self = this;
      Array.prototype.forEach.call(list.querySelectorAll('.qop-result'), function (option) {
        option.addEventListener('click', function () {
          self.pick(row, option.dataset.handle, row.querySelector('[data-qop-input]').value.trim());
        });
      });
    }

    onKeydown(event) {
      var row = event.target.closest('[data-qop-row]');
      var list = row.querySelector('[data-qop-results]');
      var options = list.hidden ? [] : list.querySelectorAll('.qop-result');

      if (event.key === 'Escape') {
        this.closeResults(row);
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        if (options.length) {
          var index = row.activeIndex >= 0 ? row.activeIndex : 0;
          this.pick(row, options[index].dataset.handle, event.target.value.trim());
        }
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      if (!options.length) return;

      event.preventDefault();
      var next = (row.activeIndex == null ? -1 : row.activeIndex) + (event.key === 'ArrowDown' ? 1 : -1);
      if (next < 0) next = options.length - 1;
      if (next >= options.length) next = 0;
      row.activeIndex = next;

      Array.prototype.forEach.call(options, function (option, i) {
        option.setAttribute('aria-selected', i === next ? 'true' : 'false');
      });
      options[next].scrollIntoView({ block: 'nearest' });
      event.target.setAttribute('aria-activedescendant', options[next].id);
    }

    closeResults(row) {
      var list = row.querySelector('[data-qop-results]');
      list.hidden = true;
      list.innerHTML = '';
      row.activeIndex = -1;
      row.querySelector('[data-qop-input]').setAttribute('aria-expanded', 'false');
    }

    closeAllResults() {
      var self = this;
      Array.prototype.forEach.call(this.rowsEl.querySelectorAll('[data-qop-row]'), function (row) {
        self.closeResults(row);
      });
    }

    setLoading(row, state) {
      var loader = row.querySelector('[data-qop-loader]') || row.querySelector('.qop-row__loader');
      if (loader) loader.hidden = !state;
    }

    /* ── resolution ── */

    pick(row, handle, query) {
      var self = this;
      this.closeResults(row);
      this.setLoading(row, true);

      fetchProduct(handle).then(function (product) {
        self.setLoading(row, false);

        if (!product) {
          self.message(row, 'That product could not be loaded.', 'error');
          return;
        }

        row.product = product;

        // An exact SKU beats everything: it is what the buyer typed.
        var variant = matchVariantBySku(product, query) || (product.variants.length === 1 ? product.variants[0] : null);

        if (!variant) {
          self.resolve(row, product, firstSellable(product), true);
          self.message(row, 'This item has options - choose the one you need.', 'note');
          return;
        }

        self.resolve(row, product, variant, product.variants.length > 1);
      });
    }

    resolve(row, product, variant, showPicker) {
      if (!variant) {
        this.message(row, 'No variant available for this product.', 'error');
        return;
      }

      row.product = product;
      row.variant = variant;
      row.classList.add('is-resolved');

      var chosen = row.querySelector('[data-qop-chosen]');
      var search = row.querySelector('.qop-row__search');
      var thumb = row.querySelector('[data-qop-thumb]');
      var title = row.querySelector('[data-qop-title]');
      var sku = row.querySelector('[data-qop-sku]');
      var picker = row.querySelector('[data-qop-variant]');

      var image = variant.featured_image ? variant.featured_image.src : product.featured_image;
      if (image) {
        thumb.src = sized(image, 120);
        thumb.alt = product.title;
        thumb.hidden = false;
      } else {
        thumb.hidden = true;
      }

      title.textContent = product.title;
      title.href = '/products/' + encodeURIComponent(product.handle);

      var parts = [];
      if (variant.sku) parts.push('SKU ' + variant.sku);
      if (product.variants.length > 1 && variant.title !== 'Default Title') parts.push(variant.title);
      sku.textContent = parts.join(' · ');

      if (showPicker) {
        picker.innerHTML = product.variants
          .map(function (option) {
            return '<option value="' + esc(option.id) + '"' +
              (option.id === variant.id ? ' selected' : '') +
              (option.available ? '' : ' disabled') + '>' +
              esc(option.title) + (option.available ? '' : ' — unavailable') +
              '</option>';
          })
          .join('');
        picker.hidden = false;
      } else {
        picker.hidden = true;
      }

      search.hidden = true;
      chosen.hidden = false;

      if (!variant.available) {
        this.message(row, 'Out of stock. Remove this line or pick another option.', 'error');
      } else {
        this.message(row, '');
      }

      this.refresh();
    }

    chooseVariant(row, id) {
      if (!row.product) return;
      var variant = row.product.variants.filter(function (option) {
        return String(option.id) === String(id);
      })[0];
      if (variant) this.resolve(row, row.product, variant, true);
    }

    unresolve(row) {
      row.product = null;
      row.variant = null;
      row.classList.remove('is-resolved');
      row.querySelector('[data-qop-chosen]').hidden = true;
      row.querySelector('.qop-row__search').hidden = false;
      this.message(row, '');

      var input = row.querySelector('[data-qop-input]');
      input.value = '';
      input.focus();
      this.refresh();
    }

    message(row, text, kind) {
      var el = row.querySelector('[data-qop-msg]');
      el.textContent = text || '';
      el.hidden = !text;
      el.className = 'qop-row__msg' + (text && kind ? ' qop-row__msg--' + kind : '');
    }

    /* ── paste ── */

    lookupPasted() {
      var self = this;
      var box = this.querySelector('.qop__paste');
      if (!box) return;

      var lines = box.value
        .split('\n')
        .map(function (line) { return line.trim(); })
        .filter(Boolean);

      if (!lines.length) {
        this.say('Paste a list first.', 'error');
        return;
      }

      this.say('Looking up ' + lines.length + ' line' + (lines.length === 1 ? '' : 's') + '…');
      this.setMode('rows');

      var missing = [];

      var work = lines.reduce(function (chain, line) {
        return chain.then(function () {
          // Comma, tab, semicolon or run of spaces - whatever the
          // buyer's spreadsheet produced when they copied it.
          var parts = line.split(/[,;\t]|\s{2,}/).map(function (part) { return part.trim(); }).filter(Boolean);
          var code = parts[0];
          var qty = parseInt(parts[1], 10);
          if (isNaN(qty) || qty < 1) qty = 1;
          if (!code) return null;

          return suggest(code).then(function (products) {
            if (!products.length) {
              missing.push(code);
              return null;
            }

            return fetchProduct(products[0].handle).then(function (product) {
              if (!product) {
                missing.push(code);
                return null;
              }

              var variant = matchVariantBySku(product, code) ||
                (product.variants.length === 1 ? product.variants[0] : firstSellable(product));

              var row = self.lastEmptyRow() || self.addRow();
              if (!row) return null;

              self.resolve(row, product, variant, product.variants.length > 1);
              row.querySelector('[data-qop-qty]').value = qty;

              if (!matchVariantBySku(product, code) && product.variants.length > 1) {
                self.message(row, 'Matched by name, not by SKU - check the option.', 'note');
              }
              return null;
            });
          });
        });
      }, Promise.resolve());

      work.then(function () {
        self.refresh();
        if (missing.length) {
          self.say('Not found: ' + missing.join(', ') + '. Everything else was added to the list.', 'error');
        } else {
          self.say('All ' + lines.length + ' lines matched. Check the quantities, then add to cart.', 'ok');
        }
        box.value = '';
      });
    }

    /* ── totals ── */

    lines() {
      var out = [];
      Array.prototype.forEach.call(this.rowsEl.querySelectorAll('[data-qop-row]'), function (row) {
        if (!row.variant || !row.variant.available) return;
        var qty = parseInt(row.querySelector('[data-qop-qty]').value, 10);
        if (isNaN(qty) || qty < 1) return;
        out.push({ row: row, variant: row.variant, quantity: qty });
      });
      return out;
    }

    refresh() {
      var self = this;
      var lines = this.lines();
      var units = 0;
      var total = 0;

      Array.prototype.forEach.call(this.rowsEl.querySelectorAll('[data-qop-row]'), function (row) {
        var priceEl = row.querySelector('[data-qop-price]');
        var subEl = row.querySelector('[data-qop-sub]');

        if (!row.variant) {
          priceEl.innerHTML = '&mdash;';
          subEl.innerHTML = '&mdash;';
          return;
        }

        var qty = parseInt(row.querySelector('[data-qop-qty]').value, 10) || 1;

        // A zero price is unset, not free. An em dash says so without
        // advertising a price the store has not published.
        if (!row.variant.price) {
          priceEl.innerHTML = '&mdash;';
          subEl.innerHTML = '&mdash;';
          return;
        }

        priceEl.textContent = money(row.variant.price, self.moneyFormat);
        subEl.textContent = money(row.variant.price * qty, self.moneyFormat);
      });

      lines.forEach(function (line) {
        units += line.quantity;
        total += (line.variant.price || 0) * line.quantity;
      });

      if (this.countEl) {
        this.countEl.textContent = lines.length
          ? lines.length + ' item' + (lines.length === 1 ? '' : 's') + ' · ' + units + ' unit' + (units === 1 ? '' : 's')
          : this.emptyLabel;
      }

      if (this.totalEl) {
        this.totalEl.hidden = !total;
        if (total) this.totalEl.textContent = money(total, this.moneyFormat);
      }

      if (this.submitEl) this.submitEl.disabled = lines.length === 0;

      /* Announced rather than pushed: anything that wants to react to
         the running total - the savings strip, for one - listens for
         this instead of the pad knowing those things exist. */
      document.dispatchEvent(
        new CustomEvent('qop:change', {
          bubbles: true,
          detail: { cents: total, items: lines.length, units: units }
        })
      );
    }

    /* ── submit ── */

    submit() {
      var self = this;
      var lines = this.lines();
      if (!lines.length) return;

      this.classList.add('is-busy');
      this.submitEl.disabled = true;
      this.say('Adding ' + lines.length + ' item' + (lines.length === 1 ? '' : 's') + '…');

      var payload = lines.map(function (line) {
        return { id: line.variant.id, quantity: line.quantity };
      });

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: payload })
      })
        .then(function (response) {
          if (response.ok) return { ok: true };
          // Shopify rejects the whole batch if one line fails, so the
          // lines are retried individually to find out which one and
          // to keep the rest of the order.
          return self.addIndividually(lines);
        })
        .then(function (result) {
          self.classList.remove('is-busy');

          if (result.ok) {
            self.say(
              lines.length + ' item' + (lines.length === 1 ? '' : 's') + ' added to your cart.',
              'ok'
            );
            self.clear();
          } else {
            self.say(result.message, 'error');
            self.refresh();
          }

          document.dispatchEvent(new CustomEvent('cart:added'));
          if (self.dataset.openDrawer === 'true' && window.CartDrawer) {
            window.CartDrawer.open();
          }
        })
        .catch(function () {
          self.classList.remove('is-busy');
          self.refresh();
          self.say('The cart could not be reached. Check your connection and try again.', 'error');
        });
    }

    addIndividually(lines) {
      var self = this;
      var failed = [];

      return lines
        .reduce(function (chain, line) {
          return chain.then(function () {
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [{ id: line.variant.id, quantity: line.quantity }] })
            })
              .then(function (response) {
                if (response.ok) return null;
                return response.json().then(function (error) {
                  var name = line.variant.sku || (line.row.product && line.row.product.title) || 'item';
                  failed.push(name);
                  self.message(line.row, (error && error.description) || 'Could not be added.', 'error');
                  return null;
                });
              })
              .catch(function () {
                failed.push(line.variant.sku || 'item');
                return null;
              });
          });
        }, Promise.resolve())
        .then(function () {
          if (!failed.length) return { ok: true };
          return {
            ok: false,
            message: 'Added everything except: ' + failed.join(', ') + '. See the lines marked below.'
          };
        });
    }

    /* ── chrome ── */

    setMode(mode) {
      Array.prototype.forEach.call(this.querySelectorAll('[data-qop-mode]'), function (button) {
        var active = button.dataset.qopMode === mode;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      Array.prototype.forEach.call(this.querySelectorAll('[data-qop-panel]'), function (panel) {
        panel.hidden = panel.dataset.qopPanel !== mode;
      });
    }

    say(text, kind) {
      if (!this.statusEl) return;
      this.statusEl.textContent = text || '';
      this.statusEl.className = 'qop__status' + (text && kind ? ' qop__status--' + kind : '');
    }
  };

  if (!customElements.get('quick-order-pad')) {
    customElements.define('quick-order-pad', QuickOrderPad);
  }
})();
